// Queries.jsx

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { parseISO, format } from 'date-fns';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'; // Added AlertTitle
import { Terminal } from 'lucide-react'; // Example icon for alert

// Hardcoded agent list for the dropdown
// Keep this consistent with your backend AGENT_MAP
const AGENT_MAPPING = {
    "kumarbookcentreonline@gmail.com": "Kumar_Book_Centre",
    "mohitvats01@gmail.com": "mohitvats01",
    "apurva000730@gmail.com": "apurva000730",
    "viveksarrthiias@gmail.com": "vivek",
    "gauravsrivastva005@gmail.com": "Gaurav",
    "subhsarrthiias@gmail.com": "subh-saarthi",
    "ravikumartanti2@gmail.com": "ravikumartanti2",
    "shafquatsarrthiias@gmail.com": "Shafquat",
    "sristiverma.rbj@gmail.com": "Sristi",
    "divyapoddar2811@gmail.com": "divya_poddar",
    "sakshisarrthiias@gmail.com": "sakshi",
    "shivani.sharma.7194@gmail.com": "shivaniSharma",
    "triloksarrthiias@gmail.com": "trilok",
    "gargivisharat@gmail.com": "Gargi",
    "vaishalisarrthiias@gmail.com": "vairshalisaarthi",
    "ankit821022@gmail.com": "ankit821022",
    "advaitsajal@gmail.com": "Advait_Sajal",
    "bhavikpunmiya@gmail.com": "Bhavik",
    "vishalsarrthiias@gmail.com": "Vishal-Mishra",
    "deepshikha1072@gmail.com": "deepshikha",
    "dummyuser@gmail.com": "dummy_user",
    "amiteshs197@gmail.com": "AMI",
    "kusumsarrthiias@gmail.com": "kusum",
    "tanunayak1902@gmail.com": "tanunayak1902",
    "shantanuesakpal1420@gmail.com": "shantanuSakpal",
    "akhileshsarrthiias@gmail.com": "Akhilesh-Kumar",
    "sonam.edsarrthi99@gmail.com": "Sonam",
    "swedhakarnani21@gmail.com": "Swedha",
    "prajwal.sarrthiias@gmail.com": "prajwal",
    "sanjaysinghrajawat23@gmail.com": "sanjaysinghrajawat23",
    "vermaanchalkumari@gmail.com": "vermaanchalkumari",
    "rkpandey817@gmail.com": "rkpandey817",
    "nikhil2906kumar@gmail.com": "Nikhil",
    "Shubham.sarrthiias@gmail.com": "Shubham",
    "shilpeesarrthiias@gmail.com": "ShilpeeKumari",
    "asarthakagarwal@gmail.com": "Sarthak_Agarwal",
    "jeetusarrthiias@gmail.com": "Jeetu",
    "manishabansal788@gmail.com": "manisha",
    "irfanmindbrink@gmail.com": "irfanmindbrink",
    "aditi90sarrthi@gmail.com": "Aditi",
    "suraj.kumarsarrthiias@gmail.com": "Suraj-saarthi",
    "harshsarrthiias@gmail.com": "harsh-saarthi",
    "kunalsarrthiias@gmail.com": "kunal",
    "sarrthiurvashi@gmail.com": "Urvashi",
    "maithanips@gmail.com": "maithanips",
    "ujjwalsarrthiias@gmail.com": "Ujjwal-singh",
    "harshmishra363@gmail.com": "Harshit",
    "saurabh@sarrthi.com": "Admin",
    "sindhu.sarrthiias@gmail.com": "sindhu",
    "rajansarrthiias@gmail.com": "Rajan-sahni",
    "shivsarrthiias@gmail.com": "Shiv",
    "bhavikpunmiya03@gmail.com": "bhavikpunmiya03",
    "snehabbc@gmail.com": "Sneha",
    "iabhaysingh15@gmail.com": "Abhay",
    "mayanksarrthiias@gmail.com": "Mayank-Saarthi",
    "sunnysarrthiias@gmail.com": "sunny",
    "nehasagar.8397@gmail.com": "Neha",
    "akashrai0561@gmail.com": "AkashRai",
    "sudhasharmasarrthiias@gmail.com": "sudhasharama",
    "puru.attrish24@gmail.com": "puruattrish24",
    // Additional agents found in data
    "santosh.sarrthiias@gmail.com": "Santosh",
    "snehasarrthiias693@gmail.com": "Sneha693",
    "rishav.sarrthiias@gmail.com": "Rishav",
    "ayushshuklasarrthiias@gmail.com": "Ayush"
};

// Removed AGENT_NAMES derivation, use Object.entries(AGENT_MAPPING) directly

const API_BASE_URL = 'https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/upiPayment'; // Define base URL

const Queries = () => {
    const [queries, setQueries] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [agent, setAgent] = useState('all'); // Stores the agent's EMAIL
    const [status, setStatus] = useState('all'); // Status filter: all, Done, Not Done
    const [limit, setLimit] = useState(20); // Page size: 20, 50, 100, 200
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExporting, setIsExporting] = useState(false); // Loading state for export

    const dateToTimestamp = (dateString) => {
      if (!dateString) return null;
      try {
          return new Date(Date.parse(dateString)).getTime().toString();
      } catch (error) {
          console.error("Date conversion error:", error);
          return null;
      }
  };

    useEffect(() => {
        fetchQueries();
    }, [page, limit]); // Refetch when page or page size changes

    const fetchQueries = async (resetPage = false) => {
        if (resetPage) {
            setPage(1); // Reset page if filters changed
        }
        setLoading(true);
        setError(null);

        const startTimestamp = dateToTimestamp(startDate);
        const endTimestamp = endDate ? dateToTimestamp(endDate + 'T23:59:59') : null; // Include full end day

        // Handle potential invalid dates from conversion
        if ((startDate && !startTimestamp) || (endDate && !endTimestamp)) {
             setLoading(false);
             // Error already set by dateToTimestamp
             return;
        }

        const params = new URLSearchParams({
            page: resetPage ? 1 : page, // Use correct page number
            limit: limit.toString(),
            status: status,
            ...(startTimestamp && { startDate: startTimestamp }),
            ...(endTimestamp && { endDate: endTimestamp }),
            ...(agent !== 'all' && { agent }), // Send the agent email
        });

        try {
            console.log(`Workspaceing: ${API_BASE_URL}/queries?${params.toString()}`);
            const res = await fetch(`${API_BASE_URL}/queries?${params.toString()}`);
            const data = await res.json();

            if (res.ok) {
                setQueries(data.queries || []);
                setTotal(data.total || 0);
            } else {
                console.error("Error fetching queries:", data.error || res.statusText);
                setError(data.error || `Failed to fetch data (Status: ${res.status})`);
                setQueries([]);
                setTotal(0);
            }
        } catch (err) {
            console.error("Network or fetch error:", err);
            setError(`Network error: ${err.message}. Please check connection and API endpoint.`);
            setQueries([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        fetchQueries(true); // Fetch data and reset to page 1
    }

    const exportCsv = async () => {
        setIsExporting(true); // Show loading state on button
        setError(null);

        const startTimestamp = dateToTimestamp(startDate);
        const endTimestamp = endDate ? dateToTimestamp(endDate + 'T23:59:59') : null;

        // Handle potential invalid dates from conversion
        if ((startDate && !startTimestamp) || (endDate && !endTimestamp)) {
             setIsExporting(false);
             // Error already set by dateToTimestamp
             return;
        }

        const params = new URLSearchParams({
            status: status,
            ...(startTimestamp && { startDate: startTimestamp }),
            ...(endTimestamp && { endDate: endTimestamp }),
            ...(agent !== 'all' && { agent }), // Send agent email
        });

        try {
            console.log(`Exporting: ${API_BASE_URL}/export-csv?${params.toString()}`);
            const res = await fetch(`${API_BASE_URL}/export-csv?${params.toString()}`);

            if (res.ok) {
                const blob = await res.blob(); // Get response as a Blob

                // Create a link to download the blob
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'queries.csv'); // Set download filename
                document.body.appendChild(link);
                link.click();

                // Clean up
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);

            } else {
                // Try to get error message from backend (assuming it sends JSON on error)
                let errorMsg = `Failed to export CSV (Status: ${res.status})`;
                try {
                    const errorData = await res.json();
                    errorMsg = errorData.error || errorMsg;
                } catch (e) {
                    // If response is not JSON, use status text
                    errorMsg = `Failed to export CSV: ${res.statusText}`;
                }
                console.error("Error exporting CSV:", errorMsg);
                setError(errorMsg);
            }
        } catch (err) {
            console.error("Network or fetch error during export:", err);
            setError(`Network error during export: ${err.message}.`);
        } finally {
            setIsExporting(false); // Hide loading state
        }
    };

    const normalizeQuery = (query) => {
        // Safely access potentially missing fields - handle both formats
        const agentEmail = query['taken_by'] || query.taken_by;
        const agentDisplayName = AGENT_MAPPING[agentEmail] || agentEmail || 'N/A';
        const updatedByEmail = query['updatedBy'] || query.updatedBy;
        const updatedByDisplayName = AGENT_MAPPING[updatedByEmail] || updatedByEmail || 'N/A';

        return {
            timestamp: query.timestamp,
            Status: query.Status || query.status || 'N/A',
            Name: query.Name || query.name || 'N/A',
            'Email ID': query['Email ID'] || query.email || 'N/A',
            Number: query.number || query.Number || 'N/A',
            'Taken By': agentDisplayName, // Display agent name in table
            'New Course Name': query['New Course Name'] || query.new_course || 'N/A',
            'Old Course Name': query['Old Course Name'] || query.current_course || 'N/A',
            Amount: query.amount || query.Price || 'N/A',
            'Course ID': query.newCourseId || 'N/A',
            UTR: query.utr || 'N/A',
            'Student/User ID': query.student_user_id || 'N/A',
            'Updated By': updatedByDisplayName,
            'Uploaded Screenshot': query['Uploaded Screenshot'] || query.file || 'N/A',
            Reason: query.Reason || query.query_desc || query.comment || 'N/A'
        };
    };

    // Client-side search filtering on the currently displayed page data
    const filteredQueries = queries.map(normalizeQuery).filter(
        (q) =>
            (q.Name?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q['Email ID']?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q.Number?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q['Taken By']?.toLowerCase() || '').includes(search.toLowerCase()) || // Search by agent display name
            (q['Updated By']?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q.UTR?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q['Student/User ID']?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q['Course ID']?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q['New Course Name']?.toLowerCase() || '').includes(search.toLowerCase()) ||
            (q.Reason?.toLowerCase() || '').includes(search.toLowerCase())
    );

    const formatDate = (dateString) => {
      if (!dateString) return "Invalid Date";
      try {
          return format(parseISO(dateString), "yyyy-MM-dd HH:mm:ss"); // Adjust format as needed
      } catch (error) {
          console.error("Date parsing error:", error);
          return "Invalid Date";
      }
  };
    return (
        <div className="container mx-auto p-4">
            <h1 className="text-2xl font-bold mb-4">UPI Payment Query Dashboard</h1>

            {error && (
                <Alert variant="destructive" className="mb-4">
                     <Terminal className="h-4 w-4" />
                     <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <div className="flex flex-wrap items-center gap-4 mb-4">
                <Input
                    placeholder="Search page..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="max-w-xs flex-grow sm:flex-grow-0"
                />
                <Select value={agent} onValueChange={setAgent}>
                    <SelectTrigger className="w-full sm:w-[200px]">
                        <SelectValue placeholder="Filter by Agent" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Agents</SelectItem>
                        {/* Sort agents by name for display */}
                        {Object.entries(AGENT_MAPPING)
                            .sort(([, nameA], [, nameB]) => nameA.localeCompare(nameB))
                            .map(([email, name]) => (
                            <SelectItem key={email} value={email}> {/* Value is the email */}
                                {name} {/* Display is the name */}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                
                <Select value={status} onValueChange={(val) => { setStatus(val); setPage(1); }}>
                    <SelectTrigger className="w-full sm:w-[150px]">
                        <SelectValue placeholder="Filter by Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="Not Done">Not Done</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val, 10)); setPage(1); }}>
                    <SelectTrigger className="w-full sm:w-[120px]">
                        <SelectValue placeholder="Page Size" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="20">20 / page</SelectItem>
                        <SelectItem value="50">50 / page</SelectItem>
                        <SelectItem value="100">100 / page</SelectItem>
                        <SelectItem value="200">200 / page</SelectItem>
                    </SelectContent>
                </Select>

                 <div className="flex flex-col sm:flex-row gap-2 items-center">
                    <span className="text-sm hidden sm:inline">Date:</span>
                    <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        placeholder="Start Date"
                         className="w-full sm:w-auto"
                     />
                      <span className="text-sm hidden sm:inline">to</span>
                     <Input
                         type="date"
                         value={endDate}
                         onChange={(e) => setEndDate(e.target.value)}
                         placeholder="End Date"
                          className="w-full sm:w-auto"
                     />
                  </div>
                 <Button variant="outline" onClick={handleRefresh} disabled={loading}>
                     {loading ? 'Refreshing...' : 'Refresh / Apply Filters'}
                 </Button>
                 <Button onClick={exportCsv} disabled={isExporting || loading}>
                     {isExporting ? 'Exporting...' : 'Export to CSV'}
                 </Button>
             </div>

             {/* Loading Indicator */}
            {loading && <div className="text-center py-10">Loading data...</div>}

            {/* No Data Message (only show if not loading and no error) */}
            {!loading && !error && queries.length === 0 && (
                <div className="text-center py-10 text-gray-500">
                    No queries found matching your criteria. Try adjusting the filters or refreshing.
                </div>
            )}

             {/* Table Display (only show if not loading, no error, and data exists) */}
            {!loading && !error && queries.length > 0 && (
                <>
                    <div className="overflow-x-auto border rounded-md">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Name</TableHead>
                                    <TableHead>Email ID</TableHead>
                                    <TableHead>Student Number</TableHead>
                                    <TableHead>Taken By</TableHead>
                                    <TableHead>New Course</TableHead>
                                    <TableHead>Old Course</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead>Course ID</TableHead>
                                    <TableHead>UTR</TableHead>
                                    <TableHead>Student/User ID</TableHead>
                                    <TableHead>Updated By</TableHead>
                                    <TableHead>Screenshot</TableHead>
                                    <TableHead>Reason</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredQueries.map((query, index) => (
                                    // Use a combination of timestamp and index for key if timestamp isn't guaranteed unique on the page
                                    <TableRow key={`${query.timestamp}-${index}`}>
                                        <TableCell className="whitespace-nowrap">{formatDate(query.timestamp)}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded text-xs ${query.Status === 'Done' || query.Status === 'DONE' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {query.Status}
                                            </span>
                                        </TableCell>
                                        <TableCell>{query.Name}</TableCell>
                                        <TableCell>{query['Email ID']}</TableCell>
                                        <TableCell>{query.Number}</TableCell>
                                        <TableCell>{query['Taken By']}</TableCell>
                                        <TableCell className="max-w-xs truncate" title={query['New Course Name']}>{query['New Course Name']}</TableCell>
                                        <TableCell className="max-w-xs truncate" title={query['Old Course Name']}>{query['Old Course Name']}</TableCell>
                                        <TableCell>{query.Amount}</TableCell>
                                        <TableCell>{query['Course ID']}</TableCell>
                                        <TableCell className="font-mono text-sm">{query.UTR}</TableCell>
                                        <TableCell>{query['Student/User ID']}</TableCell>
                                        <TableCell>{query['Updated By']}</TableCell>
                                        <TableCell>
                                            {query['Uploaded Screenshot'] && query['Uploaded Screenshot'] !== 'N/A' ? (
                                                <a
                                                    href={query['Uploaded Screenshot']}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline text-sm"
                                                    title={query['Uploaded Screenshot']} // Show full URL on hover
                                                >
                                                    View
                                                </a>
                                            ) : (
                                                'N/A'
                                            )}
                                        </TableCell>
                                        {/* Add tooltip or truncation for long reasons if needed */}
                                        <TableCell className="max-w-xs truncate" title={query.Reason}>{query.Reason}</TableCell>
                                    </TableRow>
                                ))}
                                {/* Add row if client-side search yields no results for the current page */}
                                {filteredQueries.length === 0 && queries.length > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={15} className="text-center text-gray-500">
                                            No results matching your search on this page.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center justify-between mt-4 space-x-2">
                         <span className="text-sm text-muted-foreground">
                            Total Queries: {total}
                        </span>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page === 1 || loading}
                                onClick={() => setPage(page - 1)}
                            >
                                Previous
                            </Button>
                            <span className="text-sm">
                                Page {page} of {Math.max(1, Math.ceil(total / limit))}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                disabled={page >= Math.max(1, Math.ceil(total / limit)) || loading}
                                onClick={() => setPage(page + 1)}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Queries;